import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Link } from 'components/Link/Link';
import { Colors } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { useInView } from 'react-intersection-observer';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { UpNextArticle } from 'data/SingleArticle/types';
import paths from 'constants/paths';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';
import { Spacer } from 'components/Spacer/Spacer';

const upNextImageParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '600',
};

export const UpNextCard = ({ article }: { article: UpNextArticle }) => {
  const trackEvent = useTrackEvent();
  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) {
      trackEvent('component_scrolled_into_view', {
        component: 'Up Next',
      });
    }
  }, [ref, inView]);
  const { category, image, slug, shortDescription, title } = article;

  return (
    <div data-testId="upNext-card">
      <Link
        data-testId="upNext-articleLink"
        href={paths.blogArticle(slug)}
        onClick={(event) =>
          trackEvent('linkClicked', {
            component: 'Up Next',
            linkText: title,
            href: paths.blogArticle(slug),
            path: window.location.pathname,
            posYFromTop: event.clientY,
          })
        }
      >
        <Outer ref={ref}>
          <Image image={image} alt={title} queryParams={upNextImageParams} />
          <UpNextTag>Up Next</UpNextTag>
          <TextWrapper>
            <Category>{category}</Category>
            <Title>{title}</Title>
            <ShortDescription>{shortDescription}</ShortDescription>
          </TextWrapper>
        </Outer>
      </Link>
      <Spacer hasLine={true} />
    </div>
  );
};

const Outer = styled.section`
  display: grid;
  position: relative;
  ${mediaQueries.tablet} {
    grid-template-columns: 300px 1fr;
    grid-gap: 30px;
  }
`;

const Image = styled(ArticleImage)`
  height: 200px;
  margin-bottom: 20px;
  width: 300px;
  ${mediaQueries.desktop} {
    margin-bottom: 0;
  }
`;

const UpNextTag = styled.div`
  align-items: center;
  background: linear-gradient(203.74deg, #ff4a00 42.15%, #ffc43e 101.17%);
  border-top-left-radius: 5px;
  color: ${Colors.white};
  display: flex;
  font-weight: 800;
  font-size: 12px;
  height: 30px;
  justify-content: center;
  line-height: 130%;
  position: absolute;
  text-transform: uppercase;
  width: 69px;
`;

const TextWrapper = styled.div`
  align-self: center;
`;

const Category = styled.p`
  color: ${Colors.blue500};
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  margin-bottom: 10px;
`;

const Title = styled.strong`
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  margin-bottom: 10px;
`;

const ShortDescription = styled.p`
  color: ${Colors.grey700};
  font-weight: normal;
  font-size: 16px;
  line-height: 150%;
`;
